import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Card } from '@/components/ui/Card/Card';
import { CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import { Select } from '@/components/ui/Select/Select';
import { TipTapEditor } from '@/components/admin/TipTapEditor';
import { ImageInput } from '@/components/admin/ImageInput';
import { MediaPicker } from '@/components/admin/MediaPicker';
import { useNotificationStore } from '@/stores/notificationStore';
import { blogService, generateSlug } from '@/services/blogService';
import type { BlogCategory } from '@/types/blog';

export function BlogPostEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const isNew = !id || id === 'new';

  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [ogImage, setOgImage] = useState<string | null>(null);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(null);

  // UI state
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugSuggestion, setSlugSuggestion] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isDeleting, setIsDeleting] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const slugCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load categories
  useEffect(() => {
    blogService.listCategories().then(setCategories).catch(() => {});
  }, []);

  // Load post if editing
  useEffect(() => {
    if (!isNew && id) {
      loadPost();
    }
  }, [id]);

  const loadPost = async () => {
    try {
      const post = await blogService.getPost(id!);
      setTitle(post.title);
      setSlug(post.slug);
      setContent(post.content);
      setExcerpt(post.excerpt || '');
      setFeaturedImage(post.featured_image);
      setOgImage(post.og_image);
      setMetaTitle(post.meta_title || '');
      setMetaDescription(post.meta_description || '');
      setAuthorName(post.author_name || '');
      setCategoryId(post.category_id || '');
      setStatus(post.status);
      setPublishedAt(post.published_at);
      setSlugManuallyEdited(true); // Don't auto-generate slug for existing posts
    } catch (err) {
      notify.error('Failed to load post');
      navigate('/admin/blog');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      const generated = generateSlug(title);
      setSlug(generated);
    }
  }, [title, slugManuallyEdited]);

  // Debounced slug availability check
  const checkSlugAvailability = useCallback((slugValue: string) => {
    if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
    if (!slugValue) {
      setSlugAvailable(null);
      return;
    }
    slugCheckTimeout.current = setTimeout(async () => {
      try {
        const result = await blogService.checkSlug(slugValue, isNew ? undefined : id);
        setSlugAvailable(result.available);
        setSlugSuggestion(result.suggested || null);
      } catch {
        setSlugAvailable(null);
      }
    }, 500);
  }, [id, isNew]);

  useEffect(() => {
    checkSlugAvailability(slug);
    return () => {
      if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
    };
  }, [slug, checkSlugAvailability]);

  const handleSave = async (targetStatus?: string) => {
    if (!title.trim()) {
      notify.error('Title is required');
      return;
    }
    if (!slug.trim()) {
      notify.error('Slug is required');
      return;
    }
    if (!content.trim()) {
      notify.error('Content is required');
      return;
    }

    setIsSaving(true);
    try {
      const data: Record<string, unknown> = {
        title,
        slug,
        content,
        excerpt: excerpt || undefined,
        featured_image: featuredImage,
        og_image: ogImage,
        meta_title: metaTitle || undefined,
        meta_description: metaDescription || undefined,
        author_name: authorName || undefined,
        category_id: categoryId || null,
        status: targetStatus || status,
      };

      if (isNew) {
        const post = await blogService.createPost(data as any);
        notify.success('Post created');
        navigate(`/admin/blog/${post.id}`, { replace: true });
      } else {
        await blogService.updatePost(id!, data);
        notify.success('Post saved');
        if (targetStatus) setStatus(targetStatus);
      }
    } catch (err: any) {
      notify.error(err.message || 'Failed to save post');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = () => handleSave('published');
  const handleUnpublish = async () => {
    try {
      await blogService.unpublishPost(id!);
      setStatus('draft');
      setPublishedAt(null);
      notify.success('Post unpublished');
    } catch (err: any) {
      notify.error(err.message || 'Failed to unpublish');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await blogService.deletePost(id!);
      notify.success('Post deleted');
      navigate('/admin/blog');
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete');
      setIsDeleting(false);
    }
  };

  const handleEditorImageInsert = () => {
    setImagePickerOpen(true);
  };

  const handleImageInserted = (media: { url: string; alt_text?: string }) => {
    // Insert image tag into content at cursor position
    // Since TipTap doesn't expose cursor externally easily, we append an img tag
    setContent((prev) => prev + `<img src="${media.url}" alt="${media.alt_text || ''}" />`);
  };

  if (isLoading) {
    return <PageContainer className="text-center"><span className="text-muted-foreground">Loading...</span></PageContainer>;
  }

  const categoryOptions = [
    { value: '__none__', label: 'No category' },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <PageContainer>
      <PageHeader
        title={isNew ? 'New Post' : 'Edit Post'}
        description={isNew ? 'Create a new blog post.' : 'Edit blog post content and settings.'}
      >
        <div className="flex items-center gap-2">
          {!isNew && status === 'published' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/blog/${slug}`, '_blank')}
            >
              View Live
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/blog')}>
            Back
          </Button>
          {!isNew && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave('draft')}
            disabled={isSaving}
            isLoading={isSaving && status !== 'published'}
          >
            Save Draft
          </Button>
          {status !== 'published' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handlePublish}
              disabled={isSaving}
              isLoading={isSaving}
            >
              Publish
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleSave()}
              disabled={isSaving}
              isLoading={isSaving}
            >
              Update
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        {/* Left: Main Content */}
        <div className="lg:col-span-2 space-y-5">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title"
          />

          {/* Slug */}
          <div>
            <Input
              label="Slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
              placeholder="post-url-slug"
              hint={`URL: /blog/${slug || '...'}`}
            />
            {slug && slugAvailable !== null && (
              <div className="mt-1 flex items-center gap-2">
                {slugAvailable ? (
                  <Badge variant="success">Available</Badge>
                ) : (
                  <>
                    <Badge variant="error">Taken</Badge>
                    {slugSuggestion && (
                      <button
                        className="text-xs text-primary hover:underline"
                        onClick={() => setSlug(slugSuggestion)}
                      >
                        Use "{slugSuggestion}" instead
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Content</label>
            <TipTapEditor
              content={content}
              onChange={setContent}
              placeholder="Write your blog post content..."
              onImageRequest={handleEditorImageInsert}
            />
          </div>

          <Textarea
            label="Excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Short summary for listing pages (optional)"
            rows={3}
            hint={`${excerpt.length}/500 characters`}
          />
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-5">
          {/* Featured Image */}
          <Card>
            <CardHeader>
              <CardTitle>Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageInput
                value={featuredImage}
                onChange={setFeaturedImage}
              />
            </CardContent>
          </Card>

          {/* Category & Author */}
          <Card>
            <CardHeader>
              <CardTitle>Category & Author</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select
                label="Category"
                value={categoryId || '__none__'}
                onValueChange={(v) => setCategoryId(v === '__none__' ? '' : v)}
                options={categoryOptions}
              />
              <Input
                label="Author Name"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Author display name"
              />
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  label="Meta Title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="SEO title (defaults to post title)"
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {metaTitle.length}/70
                </p>
              </div>
              <div>
                <Textarea
                  label="Meta Description"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="SEO description for search results"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {metaDescription.length}/160
                </p>
              </div>
              <ImageInput
                label="OG Image (1200x630)"
                value={ogImage}
                onChange={setOgImage}
                hint="Social sharing preview image"
              />
            </CardContent>
          </Card>

          {/* Publishing */}
          {!isNew && status === 'published' && publishedAt && (
            <Card>
              <CardHeader>
                <CardTitle>Publishing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="success">Published</Badge>
                  <span className="text-sm text-muted-foreground">
                    {new Date(publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnpublish}
                >
                  Unpublish
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Media Picker for inline images */}
      <MediaPicker
        isOpen={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={handleImageInserted}
      />
    </PageContainer>
  );
}
